import React, { useEffect, useState } from "react";
import { PiggyBank, Send, Loader2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";

/**
 * FamilyWalletTile — grid of each child's Piggy Bank balance with a 1-tap
 * "Top Up Allowance" button per kid that opens a quick transfer modal.
 * Reads from GET /api/parent/family-wallet (upstream real endpoint) and
 * hands off to the existing PIN-gated household-wallet transfer path.
 *
 * The parent dashboard passes down `kidsFallback` (from its own /kids call)
 * so we still render immediately while the family-wallet fetch is in
 * flight — no empty flash.
 */
export default function FamilyWalletTile({
  kidsFallback,
  walletAvailablePence,
  refreshKey,       // bump this on the parent side to force a re-fetch
  onTopUp,          // (kid, pennies) => void — opens the PIN modal via ParentDashboard state
}) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modalKid, setModalKid] = useState(null);
  const [amount, setAmount] = useState("1.00");
  const [modalErr, setModalErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr("");
      try {
        const { data } = await api.get("/parent/family-wallet");
        if (cancelled) return;
        setWallet(data);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e) || "Couldn't load family wallet.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // refreshKey is a monotonically-increasing integer bumped by the parent
    // dashboard on top-up / transfer / withdraw so this tile re-syncs with
    // the household wallet without a hard refresh.
  }, [refreshKey]);

  // Prefer the fresh /parent/family-wallet payload; fall back to the parent
  // dashboard's own kids[] snapshot so we can render the shell even if the
  // wallet call is delayed.
  const kids = (wallet?.kids && wallet.kids.length > 0)
    ? wallet.kids.map((k) => ({
        id: k.kid_id || k.id,
        name: k.name || k.kid_name,
        avatar_emoji: k.avatar_emoji || "🧒",
        age: k.age,
        wallet_pennies: k.wallet_pennies ?? 0,
        handle: k.handle,
      }))
    : (kidsFallback || []);

  if (!Array.isArray(kids) || kids.length === 0) {
    if (loading) {
      return (
        <div className="card-base p-6 md:p-8 mt-8" data-testid="family-wallet-tile">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading family wallet&hellip;
          </div>
        </div>
      );
    }
    return null;
  }

  const total = kids.reduce((sum, k) => sum + (k.wallet_pennies || 0), 0);

  const open = (kid) => { setAmount("1.00"); setModalErr(""); setModalKid(kid); };
  const close = () => setModalKid(null);

  const submit = (e) => {
    e.preventDefault();
    setModalErr("");
    const parsed = parseFloat(amount);
    if (!(parsed > 0)) { setModalErr("Enter an amount greater than £0."); return; }
    if (parsed > 250) { setModalErr("Single top-ups are capped at £250. Split across multiple transfers if you need more."); return; }
    const pennies = Math.round(parsed * 100);
    if (pennies > walletAvailablePence) {
      setModalErr(`Household wallet only has ${formatGBP(walletAvailablePence / 100)} available. Top up first.`);
      return;
    }
    onTopUp(modalKid, pennies);
    close();
  };

  return (
    <>
      <div className="card-base p-6 md:p-8 mt-8" data-testid="family-wallet-tile">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandPink text-white shadow-sm">
              <PiggyBank className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900">Family Wallet</h2>
              <p className="text-xs text-slate-500 mt-0.5">Each kid&rsquo;s Piggy Bank at a glance.</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Family total</div>
            <div className="text-xl font-display font-semibold text-emerald-800" data-testid="family-wallet-total">{formatGBP(total / 100)}</div>
          </div>
        </div>

        {err && (
          <div className="mb-3 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-sm text-rose-800" data-testid="family-wallet-error">{err}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="family-wallet-grid">
          {kids.map((k) => (
            <div
              key={k.id}
              className="rounded-2xl bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/10 border border-brandTeal/20 p-4 flex items-center gap-3"
              data-testid={`family-wallet-kid-${k.id}`}
            >
              <div className="w-11 h-11 rounded-full bg-white shadow-sm border border-slate-100 inline-flex items-center justify-center text-xl flex-shrink-0">
                {k.avatar_emoji || "🧒"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 truncate leading-tight">{k.name}</div>
                {k.handle && <div className="font-mono text-[11px] text-brandTealDark font-bold mt-0.5">@{k.handle}</div>}
                <div
                  className="text-lg font-display font-semibold text-emerald-800 leading-tight mt-1"
                  data-testid={`family-wallet-balance-${k.id}`}
                >
                  {formatGBP((k.wallet_pennies || 0) / 100)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => open(k)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold shadow-sm transition flex-shrink-0"
                data-testid={`family-wallet-topup-btn-${k.id}`}
              >
                <Send className="w-3.5 h-3.5" /> Top Up
              </button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!modalKid} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="family-wallet-topup-modal">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-brandTeal/10 via-white to-brandPink/10 border-b border-slate-100">
            <div className="pill bg-brandTeal/15 text-brandTeal"><PiggyBank className="w-3 h-3 mr-1" /> Top Up Allowance</div>
            <DialogHeader className="mt-2 space-y-1 text-left">
              <DialogTitle className="font-display text-2xl font-semibold text-brandSlate">
                Send pocket money to {modalKid?.name}
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600 leading-relaxed">
                It arrives in {modalKid?.name}&rsquo;s Piggy Bank the moment you confirm with your PIN.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={submit} noValidate className="px-6 pt-5 pb-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-brandSlate mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">£</span>
                <input
                  type="number"
                  min="0.5" step="0.5" inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-base pl-7"
                  data-testid="family-wallet-topup-amount"
                  autoFocus
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["1", "2", "5", "10"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(`${v}.00`)}
                    className="px-3 py-1 rounded-full bg-slate-100 hover:bg-brandTeal/10 hover:text-brandTeal text-xs font-bold text-slate-700 transition"
                    data-testid={`family-wallet-topup-quick-${v}`}
                  >
                    £{v}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Household wallet available: <span className="font-bold text-slate-700">{formatGBP(walletAvailablePence / 100)}</span>
              </p>
            </div>
            {modalErr && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="family-wallet-topup-error">{modalErr}</div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={close} className="flex-1 btn-secondary" data-testid="family-wallet-topup-cancel">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" className="flex-1 btn-primary" data-testid="family-wallet-topup-submit">
                <Check className="w-4 h-4" /> Send with PIN
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
